import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const progressBarVariants = cva(
  'w-full rounded-full overflow-hidden bg-muted',
  {
    variants: {
      size: {
        sm: 'h-1',
        default: 'h-2',
        lg: 'h-3',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const colorMap = {
  lime: 'bg-[var(--color-lime)]',
  cyan: 'bg-[var(--color-cyan)]',
  pink: 'bg-[var(--color-pink)]',
  purple: 'bg-[var(--color-purple)]',
  orange: 'bg-[var(--color-orange)]',
  gradient: 'bg-[linear-gradient(to_right,var(--color-cyan),var(--color-purple))]',
} as const

export interface ProgressBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>,
    VariantProps<typeof progressBarVariants> {
  value: number
  max?: number
  color?: keyof typeof colorMap
  showLabel?: boolean
  /**
   * Where to render the numeric label when `showLabel` is true.
   * `inline` (default) keeps the existing behavior — a fixed-width label to the
   * right of the track, on the same row. `below` right-aligns the label on its
   * own line underneath the track (used by the Vitals drawer's compact rows).
   */
  labelPlacement?: 'inline' | 'below'
}

const ProgressBar = React.forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      className,
      size,
      value,
      max = 100,
      color = 'lime',
      showLabel = false,
      labelPlacement = 'inline',
      ...props
    },
    ref
  ) => {
    const percentage = Math.min(Math.max((value / max) * 100, 0), 100)
    const track = (
      <div className={cn(progressBarVariants({ size }), 'flex-1')}>
        <div
          className={cn('h-full rounded-full transition-all duration-500', colorMap[color])}
          style={{ width: `${percentage}%` }}
        />
      </div>
    )

    if (labelPlacement === 'below') {
      return (
        <div className={cn('flex flex-col gap-1', className)} ref={ref} {...props}>
          {track}
          {showLabel && (
            <span className="text-right text-xs font-mono text-muted-foreground">
              {percentage.toFixed(0)}%
            </span>
          )}
        </div>
      )
    }

    return (
      <div className={cn('flex items-center gap-2', className)} ref={ref} {...props}>
        {track}
        {showLabel && (
          <span className="text-xs font-mono text-muted-foreground w-10 text-right">
            {percentage.toFixed(0)}%
          </span>
        )}
      </div>
    )
  }
)
ProgressBar.displayName = 'ProgressBar'

export { ProgressBar, progressBarVariants }
